import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Mail, RotateCw, Search } from "lucide-react";
import { JobNumberLink } from "@/components/JobNumberLink";
import { ChecklistEmailDialog } from "@/components/forms/ChecklistEmailDialog";
import { toast } from "sonner";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const day = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

// Log of every checklist email sent to clients, with a resend (preview → send) button.
export const SentEmailsLog = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [resend, setResend] = useState(null);
  const load = useCallback((term) => {
    const tag = (load.seq = (load.seq || 0) + 1);
    client.get("/checklist-email/log", { params: term ? { q: term } : {} })
      .then((r) => { if (tag === load.seq) setRows(r.data); })
      .catch(() => { if (tag === load.seq) { setRows([]); toast.error("Could not load sent emails"); } });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = setTimeout(() => load(q), 250); return () => clearTimeout(h); }, [q, load]);

  return (
    <Card className="border-slate-200" data-testid="sent-emails-log">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b">
        <p className="font-semibold text-slate-900 flex items-center gap-2"><Mail className="h-4 w-4 text-brand" />Sent checklist emails {rows && <span className="text-sm font-normal text-slate-500" data-testid="sent-emails-count">({rows.length})</span>}</p>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client, address, job # or email" className="pl-8 h-9" data-testid="sent-emails-search" />
        </div>
      </div>
      {rows === null ? <p className="p-6 text-sm text-slate-400">Loading…</p> : rows.length === 0 ? (
        <p className="p-6 text-sm text-slate-500" data-testid="sent-emails-empty">{q.trim() ? `No emails match "${q.trim()}".` : "No checklist emails sent yet."}</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Sent</TableHead><TableHead>Client</TableHead><TableHead>Job</TableHead><TableHead>Checklist</TableHead>
              <TableHead>Date on PDF</TableHead><TableHead>Sent to</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`sent-email-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{when(r.at)}<div className="text-xs text-slate-400">{r.by}</div></TableCell>
                  <TableCell className="text-sm"><div className="font-medium text-slate-800">{r.clientName || "—"}</div><div className="text-xs text-slate-500 max-w-[220px] truncate">{r.address}</div></TableCell>
                  <TableCell>{r.jobNumber ? <JobNumberLink n={r.jobNumber} testid={`sent-email-job-${r.id}`} /> : "—"}</TableCell>
                  <TableCell className="text-sm max-w-[180px] truncate">{r.checklist}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{day(r.shownDate)}</TableCell>
                  <TableCell className="text-sm"><div>{r.to}</div>{r.copyTo && <div className="text-xs text-slate-400">copy: {r.copyTo}</div>}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" disabled={!r.canResend} onClick={() => setResend(r)} title={r.canResend ? "Preview and send again" : "The checklist no longer exists"} data-testid={`sent-email-resend-${r.id}`}>
                      <RotateCw className="h-3.5 w-3.5 mr-1" />Resend
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ChecklistEmailDialog iid={resend?.instanceId} initialDate={resend?.shownDate} onOpenChange={(o) => !o && setResend(null)} onSent={() => load(q)} />
    </Card>
  );
};
